import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import type { CompetitiveLandscape } from '@magig/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { OnboardingBack, OnboardingExit, PageEyebrow } from '../components/auth-shells.js';
import { Mag } from '../components/mag.js';
import {
  type BrandBrainEnvelope,
  type CompetitionAnalyzeOutcome,
  fetchBrandMarkets,
  requestCompetitionAnalysis,
  saveBrandBrain,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { founderErrorCopy, isOutOfTokens } from '../lib/error-copy.js';
import { countryLabel } from '../lib/market-options.js';
import { billingQuery, brandBrainQuery, brandsQuery, meQuery } from '../lib/queries.js';

// Onboarding step 4 — Mag's competition pass. Three phases, all
// persisted in the URL so refresh / back-nav doesn't reset the
// founder's progress within this step:
//
//   - intro: explain what mag's about to do, let the founder
//     optionally paste competitor URLs / a hint, then click "start"
//   - running: ~30-60s loading state with cycling phrases + a stall
//     fallback message if it crosses STALL_THRESHOLD_MS
//   - results: the landed CompetitiveLandscape — summary + top
//     competitors + top recommendation, then "continue to home"
//
// Refreshing on ?phase=running polls the brain instead of re-firing
// the mutation (the original server-side run may still be in flight,
// or may have completed during a network hiccup). The moment a
// landscape lands on brain.competitiveLandscape we flip to results.

// 4 min. Real runs (web_search + multi-competitor fetch + Sonnet
// synthesis) regularly take 2min+ — one observed run was 133s for 4
// competitors. At 120s the page cried "stalled" on perfectly healthy
// runs (and right as the result was about to land). 4min is well clear
// of any plausible run while still catching a genuine hang.
const STALL_THRESHOLD_MS = 240_000;

// Mag's competition pass has four observable stages on the server
// side: web-search → fetch competitor sites → cross-compare → write
// recommendations. We map each stage to a time slice against the
// ESTIMATED_DURATION_MS budget so the UI can show concrete "step N
// of 4" feedback that tracks roughly with what the agent is doing,
// even though we can't stream real progress events from Sonnet.
// ~2min budget — matches observed run times (web_search + multi-
// competitor fetch + Sonnet synthesis lands around 130s). The earlier
// 60s budget left the bar pinned at "almost there" for over a minute on
// every real run.
const ESTIMATED_DURATION_MS = 120_000;
const RUNNING_STAGES: ReadonlyArray<{ label: string; throughMs: number }> = [
  { label: 'searching the web for competitors', throughMs: 30_000 },
  { label: 'opening their sites + reading hero copy', throughMs: 65_000 },
  { label: 'comparing your moves to theirs', throughMs: 95_000 },
  { label: 'writing the playbook', throughMs: ESTIMATED_DURATION_MS },
];

function stageForElapsed(elapsedMs: number): { index: number; label: string } {
  for (let i = 0; i < RUNNING_STAGES.length; i += 1) {
    const stage = RUNNING_STAGES[i];
    if (!stage) continue;
    if (elapsedMs < stage.throughMs) return { index: i, label: stage.label };
  }
  const last = RUNNING_STAGES[RUNNING_STAGES.length - 1];
  return {
    index: RUNNING_STAGES.length - 1,
    label: last ? `${last.label} — almost there` : 'finishing up',
  };
}

type Phase = 'intro' | 'running' | 'results';

interface CompetitionSearch {
  onboarding?: '1';
  phase?: Phase;
}

export const Route = createFileRoute('/brand/$slug/onboarding/competition')({
  validateSearch: (search: Record<string, unknown>): CompetitionSearch => {
    const out: CompetitionSearch = {};
    if (search['onboarding'] === '1') out.onboarding = '1';
    const phase = search['phase'];
    if (phase === 'intro' || phase === 'running' || phase === 'results') {
      out.phase = phase;
    }
    return out;
  },
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    return { brand };
  },
  component: CompetitionStep,
});

function CompetitionStep() {
  const { brand } = Route.useRouteContext();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  useBrandTitle(brand.slug, ['onboarding', 'competition']);

  // Brain query — polled while running so a refresh / network hiccup
  // can still detect completion server-side.
  const { data: envelope } = useQuery({
    ...brandBrainQuery(brand.id),
    refetchInterval: search.phase === 'running' ? 3_000 : false,
    refetchIntervalInBackground: false,
  });
  const existingLandscape = envelope?.brandBrain?.competitiveLandscape ?? null;

  // Phase: URL is source of truth, but we promote to "results" the
  // moment a landscape lands (whether via the mutation or via polling).
  const phase: Phase = search.phase ?? (existingLandscape ? 'results' : 'intro');

  const [competitorInput, setCompetitorInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<CompetitionAnalyzeOutcome | null>(null);
  const [runStartedAtMs, setRunStartedAtMs] = useState<number | null>(null);

  // Refresh lands the founder on ?phase=running with local state lost;
  // bootstrap a fresh start timestamp so the progress bar / stall
  // detector still have a baseline. Server-side the run is either
  // already in flight or finished, polling picks up the result.
  useEffect(() => {
    if (phase === 'running' && runStartedAtMs === null) {
      setRunStartedAtMs(Date.now());
    }
  }, [phase, runStartedAtMs]);

  // Heartbeat so the stall threshold UI updates without depending on
  // a re-render driven by the mutation.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (phase !== 'running') return;
    const id = window.setInterval(() => setNowMs(Date.now()), 5_000);
    return () => window.clearInterval(id);
  }, [phase]);
  const stalled =
    phase === 'running' && runStartedAtMs !== null && nowMs - runStartedAtMs > STALL_THRESHOLD_MS;

  const goToPhase = (next: Phase) => {
    void navigate({
      to: '/brand/$slug/onboarding/competition',
      params: { slug: brand.slug },
      search: { ...search, phase: next },
      replace: true,
    });
  };

  const mutation = useMutation({
    mutationFn: (args: { namedCompetitorUrls: string[]; founderHints?: string }) =>
      requestCompetitionAnalysis(brand.id, args),
    onSuccess: () => {
      // The endpoint now fires the analyst in the BACKGROUND and returns
      // immediately (started: true) — there's no synchronous result to
      // mirror. Stay in 'running': the brain poll (every 3s) flips us to
      // 'results' the moment competitiveLandscape lands, and the stall
      // banner covers a genuine hang after STALL_THRESHOLD_MS. handleStart
      // has already moved us to 'running' + stamped runStartedAtMs.
    },
    onError: (err) => {
      // A real competition run can take ~2min+ — longer than the edge's
      // request timeout — so the browser can throw "Failed to fetch"
      // while the backend keeps running and writes the landscape to the
      // brain (confirmed in prod: client saw the drop at ~100s, the
      // agent completed at 133s and landed a card). On a transient
      // network drop DON'T bail to intro — that stops the brain poll
      // that would otherwise catch the result. Stay in 'running'; the
      // poll flips to results when the landscape lands, and the stall
      // banner covers a genuine hang. Only surface fast, real errors.
      // NOT `msg` — that is the lingui macro's name, and a local by that
      // name shadows it silently. The merge restored the old name; tsc only
      // caught it because the body still referenced `detail`.
      // OUT OF MAG TOKENS IS NOT AN ERROR, IT IS THE PAYWALL ARRIVING
      // EARLY. Left to the generic branch it rendered "top up at the
      // dashboard" — advice a founder in free onboarding cannot take,
      // since the dashboard is precisely what the paywall gates — beside
      // a retry that would fail identically forever. Send them to the
      // picker they were walking toward anyway; the competition step is
      // the LAST one before it (see goNext below), so this is the same
      // destination one step early, with a reason attached.
      if (isOutOfTokens(err)) {
        void navigate({
          to: '/onboarding/plan',
          search: { brand: brand.slug, reason: 'out_of_tokens' },
        });
        return;
      }
      const detail = founderErrorCopy(err);
      const transient =
        /failed to fetch|networkerror|load failed|timed? ?out|aborted|connection|err_/i.test(
          detail,
        );
      if (transient) return;
      setError(detail || i18n._(msg`something broke. let me try again.`));
      goToPhase('intro');
    },
  });

  // Detect landscape landing via polling (the mutation's response can
  // be lost on refresh / nav). The moment it lands, switch to results.
  // goToPhase is captured by ref so the effect can call the latest
  // navigate without re-firing every render when navigate/search change.
  const lastSeenLandscapeRef = useRef<CompetitiveLandscape | null>(existingLandscape);
  const goToPhaseRef = useRef(goToPhase);
  goToPhaseRef.current = goToPhase;
  useEffect(() => {
    if (phase !== 'running') return;
    if (existingLandscape && existingLandscape !== lastSeenLandscapeRef.current) {
      lastSeenLandscapeRef.current = existingLandscape;
      goToPhaseRef.current('results');
    }
  }, [existingLandscape, phase]);

  const handleStart = () => {
    setError(null);
    setOutcome(null);
    setRunStartedAtMs(Date.now());
    const urls = parseCompetitorUrls(competitorInput);
    goToPhase('running');
    mutation.mutate({
      namedCompetitorUrls: urls,
      ...(competitorInput.trim() && urls.length === 0
        ? { founderHints: competitorInput.trim().slice(0, 2000) }
        : {}),
    });
  };

  const handleRetry = () => {
    setError(null);
    setOutcome(null);
    goToPhase('intro');
  };

  // Value-first reorder: competition is the LAST onboarding step, so
  // this is where the paywall lands. Founders who haven't subscribed go
  // to /onboarding/plan (carrying the brand slug so checkout can return
  // to this brand's home). Already-subscribed founders — e.g. a Growth
  // user onboarding a 2nd brand — skip straight to home.
  const goNext = async () => {
    const billing = await queryClient.ensureQueryData(billingQuery());
    const paid =
      billing.subscriptionStatus === 'active' || billing.subscriptionStatus === 'trialing';
    if (paid) {
      await navigate({ to: '/brand/$slug/home', params: { slug: brand.slug } });
    } else {
      await navigate({ to: '/onboarding/plan', search: { brand: brand.slug } });
    }
  };

  const handleSkip = goNext;
  const handleContinue = goNext;

  return (
    <main className="relative min-h-screen bg-[var(--cream)]">
      <OnboardingBack
        to="/brand/$slug/onboarding/markets"
        brandSlug={brand.slug}
        search={{ onboarding: '1' as const }}
      >
        <Trans>back to markets</Trans>
      </OnboardingBack>
      <OnboardingExit brandSlug={brand.slug} />

      <section className="mx-auto max-w-[720px] px-4 py-12 sm:px-6">
        <div className="mag-stagger-in" style={{ animationDelay: '80ms' }}>
          <PageEyebrow>
            <Trans>onboarding · step 6 of 6 · competition</Trans>
          </PageEyebrow>
        </div>

        {phase === 'intro' ? (
          <IntroPhase
            brandName={brand.name}
            competitorInput={competitorInput}
            onCompetitorInputChange={setCompetitorInput}
            onStart={handleStart}
            onSkip={handleSkip}
            error={error}
            skippedOutcome={outcome}
          />
        ) : null}

        {phase === 'running' ? (
          <RunningPhase
            brandName={brand.name}
            stalled={stalled}
            runStartedAtMs={runStartedAtMs}
            onWait={() => setRunStartedAtMs(Date.now())}
            onSkip={handleSkip}
            onRetry={handleRetry}
          />
        ) : null}

        {phase === 'results' && existingLandscape ? (
          <ResultsPhase
            landscape={existingLandscape}
            brandId={brand.id}
            onContinue={handleContinue}
          />
        ) : null}
      </section>
    </main>
  );
}

// ─── Intro phase ──────────────────────────────────────────────────

function IntroPhase({
  brandName,
  competitorInput,
  onCompetitorInputChange,
  onStart,
  onSkip,
  error,
  skippedOutcome,
}: {
  brandName: string;
  competitorInput: string;
  onCompetitorInputChange: (v: string) => void;
  onStart: () => void;
  onSkip: () => void;
  error: string | null;
  skippedOutcome: CompetitionAnalyzeOutcome | null;
}) {
  const { t } = useLingui();

  // Competitor URLs are optional — Mag auto-discovers. Keep that input
  // tucked behind a toggle so the primary path is just "let her find
  // them"; it only opens if the founder already has names in mind (or
  // re-opens when they'd typed some before a failed run).
  const [showInput, setShowInput] = useState(competitorInput.trim().length > 0);

  return (
    <>
      <div className="mt-3 flex items-start gap-4">
        <div className="mag-stagger-in relative shrink-0" style={{ animationDelay: '120ms' }}>
          <div
            aria-hidden="true"
            className="mag-aura pointer-events-none absolute inset-[-12px] rounded-full"
          />
          <div className="mag-breathe relative">
            <Mag size={64} mood="casting" trackCursor />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <h1
            className="mag-stagger-in font-display text-[34px] sm:text-[48px] font-black leading-[1.02] tracking-[-0.04em]"
            style={{ animationDelay: '180ms' }}
          >
            <Trans>
              one last thing — <span className="text-[var(--plum)]">who you're up against.</span>
            </Trans>
          </h1>
          <p
            className="mag-stagger-in mt-3 font-serif text-[17px] leading-[1.5] text-[var(--ink)]/80"
            style={{ animationDelay: '260ms' }}
          >
            <Trans>
              “i'll search the web for who else is selling what {brandName.toLowerCase()} sells,
              read their sites, and write a swot + the moves i'd make. takes a minute or two.”
            </Trans>
          </p>
        </div>
      </div>

      {error || (skippedOutcome && skippedOutcome.outcome !== 'card_created') ? (
        <aside
          className="mag-stagger-in mt-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-4 shadow-[4px_4px_0_var(--pulse)]"
          style={{ animationDelay: '320ms' }}
        >
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--pulse)]">
            {'↘'} <Trans>last run</Trans>
          </p>
          <p className="mt-2 font-serif text-[14px] leading-[1.45] text-[var(--ink)]/80">
            “{error ?? skipReason(skippedOutcome)}”
          </p>
        </aside>
      ) : null}

      <div className="mag-stagger-in mt-8" style={{ animationDelay: '380ms' }}>
        {showInput ? (
          <label className="field">
            <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/70">
              <Trans>competitors you already know (optional)</Trans>
            </span>
            <textarea
              value={competitorInput}
              onChange={(e) => onCompetitorInputChange(e.target.value)}
              // [8140542] \n, not &#10;. A JSX attribute fed from a JS
              // expression is never HTML-decoded, so the entities rendered as
              // five literal characters in the textarea — and sat in both
              // catalogs for a translator to preserve by hand.
              placeholder={t`https://competitor-one.com\nhttps://competitor-two.com\n…`}
              rows={4}
              // biome-ignore lint/a11y/noAutofocus: opens on explicit click; typing is the next intended action
              autoFocus
            />
            <span className="field-hint">
              <Trans>
                one url per line. mag analyzes these plus auto-discovers others. lines that don't
                look like urls get used as a free-text hint instead.
              </Trans>
            </span>
          </label>
        ) : (
          <button
            type="button"
            onClick={() => setShowInput(true)}
            className="flex w-full items-center justify-between gap-3 rounded-xl border border-[var(--ink)]/12 bg-white/50 px-4 py-3 text-left transition hover:border-[var(--ink)]/30"
          >
            <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
              {'↘'} <Trans>already know some competitors?</Trans>
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/45">
              <Trans>name them ↓</Trans>
            </span>
          </button>
        )}
      </div>

      <div
        className="mag-stagger-in mt-6 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end"
        style={{ animationDelay: '460ms' }}
      >
        <button
          type="button"
          onClick={onSkip}
          className="btn btn-ghost px-4 py-2 text-[12px] sm:order-1"
        >
          <Trans>skip for now</Trans>
        </button>
        <button type="button" onClick={onStart} className="btn btn-lg sm:order-2">
          <Trans>size them up</Trans>
          <span className="btn-arrow">→</span>
        </button>
      </div>
    </>
  );
}

// ─── Running phase ────────────────────────────────────────────────

function RunningPhase({
  brandName,
  stalled,
  runStartedAtMs,
  onWait,
  onSkip,
  onRetry,
}: {
  brandName: string;
  stalled: boolean;
  runStartedAtMs: number | null;
  onWait: () => void;
  onSkip: () => void;
  onRetry: () => void;
}) {
  // Time-based progress proxy. The Sonnet agent is opaque — we can't
  // stream real intermediate findings — so we approximate the four
  // observable stages of work against an ESTIMATED_DURATION_MS budget
  // and let the founder watch a bar fill. Capped at 95% until the
  // mutation resolves so the bar never claims "done" before the data
  // lands. This is honest signal (the agent IS doing this work, in
  // this order) without requiring the agent to emit progress events.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 500);
    return () => window.clearInterval(id);
  }, []);
  const elapsedMs = runStartedAtMs ? Math.max(0, nowMs - runStartedAtMs) : 0;
  const elapsedSec = Math.floor(elapsedMs / 1000);
  const stage = stageForElapsed(elapsedMs);
  const progressPct = stalled
    ? 95
    : Math.min(95, Math.round((elapsedMs / ESTIMATED_DURATION_MS) * 100));

  return (
    <>
      <div className="mt-3 flex items-start gap-4">
        <div className="mag-stagger-in shrink-0" style={{ animationDelay: '120ms' }}>
          <Mag size={64} mood="casting" trackCursor />
        </div>
        <div className="min-w-0 flex-1">
          <h1
            className="mag-stagger-in font-display text-[34px] sm:text-[48px] font-black leading-[1.02] tracking-[-0.04em]"
            style={{ animationDelay: '180ms' }}
          >
            <Trans>sizing up {brandName.toLowerCase()}'s competition.</Trans>
          </h1>
          <p
            className="mag-stagger-in mt-3 font-serif text-[17px] leading-[1.5] text-[var(--ink)]/80"
            style={{ animationDelay: '260ms' }}
          >
            <Trans>
              “searching the web, opening their sites, reading what they sell. give me a minute.”
            </Trans>
          </p>
        </div>
      </div>

      {!stalled ? (
        <div
          className="mag-stagger-in mt-8 flex flex-col gap-3 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--plum)]"
          style={{ animationDelay: '340ms' }}
        >
          <div className="flex items-baseline justify-between gap-3">
            <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              <Trans>
                ↘ step {stage.index + 1} of {RUNNING_STAGES.length} · {stage.label}
              </Trans>
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
              <Trans>{elapsedSec}s</Trans>
            </p>
          </div>

          {/* Time-budget progress bar — fills linearly to 95%; the final
              5% is reserved for "result landing" so the bar never tops
              out before the data does. */}
          <div className="h-[5px] w-full overflow-hidden rounded-full bg-[var(--ink)]/8">
            <div
              className="h-full rounded-full bg-[var(--plum)] transition-[width] duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {/* Four-stage receipt list — each stage flips to a ✓ once its
              time slice closes, so the founder sees concrete "this step
              done" feedback rather than just a moving bar. The check
              uses the brand SVG glyph (same as BrowserWindow's address
              bar) so the stroke stays crisp at 16px and centers cleanly
              — the unicode ✓ char doesn't render consistently at that
              size and was floating off-center. */}
          <ul className="mt-1 flex flex-col gap-1.5">
            {RUNNING_STAGES.map((s, i) => {
              const isDone = i < stage.index;
              const isActive = i === stage.index;
              const status = isDone ? 'done' : isActive ? 'active' : 'pending';
              return (
                <li key={s.label} className="flex items-center gap-2">
                  {/* Status-keyed remount fires mag-check-pop fresh on the
                      active → done transition (the keyframe runs only on
                      mount; without the key the class is already applied
                      and React skips the animation). mag-pulse is the
                      brand's pulse (1.4s ease-in-out) — used instead of
                      Tailwind's animate-pulse for active. Background +
                      border transitions smooth the color flip between
                      transitions. */}
                  <span
                    key={status}
                    aria-hidden="true"
                    className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-[background-color,border-color] duration-300 ease-out ${
                      isDone
                        ? 'mag-check-pop bg-[var(--sage)]'
                        : isActive
                          ? // mag-pulse-soft (opacity only) instead of mag-pulse —
                            // the scale variant overflows the icon's 16px slot and
                            // reads as left-shifted against the other rows.
                            'mag-pulse-soft bg-[var(--plum)]'
                          : 'border border-[var(--ink)]/20 bg-transparent'
                    }`}
                  >
                    {isDone ? (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="white"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        focusable="false"
                      >
                        {/* Polyline geometry centered on viewBox center (8,8).
                            bbox: x 4-12 (center 8), y 6-11 (center 8.5).
                            Elbow at (7,11) sits slightly below center so the
                            visual weight balances against the upper-right
                            tip — a check's mass reads heaviest at its
                            elbow, so geometric-center bbox alone leans up. */}
                        <polyline points="4 8 7 11 12 6" />
                      </svg>
                    ) : null}
                  </span>
                  <span
                    className={`font-mono text-[10.5px] uppercase tracking-[0.08em] transition-colors duration-300 ease-out ${
                      isDone
                        ? 'text-[var(--ink)]/55'
                        : isActive
                          ? 'text-[var(--plum)]'
                          : 'text-[var(--ink)]/30'
                    }`}
                  >
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <aside className="mag-stagger-in mt-8 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--pulse)]">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--pulse)]">
            {'↘'} <Trans>this is taking longer than usual</Trans>
          </p>
          <p className="mt-2 font-serif text-[14px] leading-[1.5] text-[var(--ink)]/80">
            <Trans>
              “web searches sometimes drag. i'm still working — give me another minute, or skip and
              i'll let you know on home when i'm done.”
            </Trans>
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={onSkip} className="btn btn-ghost btn-sm">
              <Trans>skip — i'll check home</Trans>
            </button>
            <button type="button" onClick={onRetry} className="btn btn-ghost btn-sm">
              <Trans>start over</Trans>
            </button>
            <button type="button" onClick={onWait} className="btn btn-sm">
              <Trans>give it another minute</Trans>
            </button>
          </div>
        </aside>
      )}
    </>
  );
}

// ─── Results phase ────────────────────────────────────────────────
// Informational only during onboarding — Mag shows the founder what
// she pulled (competitors, summary, recommendations) but DOESN'T ask
// them to accept/discard yet. Triage lives post-onboarding on /home
// as "recommended next actions" so the founder isn't context-
// switching between brand-setup and strategic-decision modes during
// onboarding. The schema fields (id/status/acceptedAt on each rec)
// are stamped server-side regardless so the home surface has the
// data model ready when it lands next session.

// Distinct badge per competitor category so the founder reads the
// threat shape at a glance: direct (same product/audience) = plum,
// indirect (alternative solution) = amber, search (ranks for the same
// terms) = muted ink.
function categoryChipClass(category: string): string {
  switch (category) {
    case 'direct':
      return 'border-[var(--plum)]/30 bg-[var(--plum)]/12 text-[var(--plum)]';
    case 'indirect':
      return 'border-[#d4a017]/35 bg-[#d4a017]/12 text-[#7a5a05]';
    case 'search':
      return 'border-[var(--ink)]/20 bg-[var(--ink)]/[0.06] text-[var(--ink)]/55';
    default:
      return 'border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]/60';
  }
}

function ResultsPhase({
  landscape,
  brandId,
  onContinue,
}: {
  landscape: CompetitiveLandscape;
  brandId: string;
  onContinue: () => void;
}) {
  const { t } = useLingui();

  const actions = useCompetitorActions(brandId);

  // [spec 26a1cf5c / multi-market] This screen shows ONE market's read — the
  // primary, because that is the only pass the onboarding step waits for.
  // runCompetitionForBrandMarkets runs the primary synchronously and fans the
  // rest out behind it at roughly two minutes each, so on a six-market brand
  // five of them are still running while the founder reads this.
  //
  // Without naming the market, "3 competitors landed" reads as the whole
  // answer and a founder who picked six markets concludes the other five were
  // ignored. They are not: each lands as its own competition card on /home,
  // where feed-card-view already renders a country chip from
  // payload.marketCountry. The only thing missing was saying so here.
  const { data: markets = [] } = useQuery({
    queryKey: ['brand-markets', brandId],
    queryFn: () => fetchBrandMarkets(brandId),
    // Pure decoration on this screen: a failed lookup must not keep the
    // founder from reading their competitors or pressing continue.
    retry: false,
  });
  const primaryMarket = markets.find((m) => m.isPrimary) ?? markets[0];
  const primaryLabel = primaryMarket ? countryLabel(primaryMarket.country) : null;
  const pendingMarkets = markets.filter((m) => !m.isPrimary);
  const pendingLabel = pendingMarkets.map((m) => countryLabel(m.country)).join(', ');

  const visibleCompetitors = landscape.competitors.slice(0, 6);
  const remainingCompetitors = Math.max(
    0,
    landscape.competitors.length - visibleCompetitors.length,
  );
  // [Layer 1] Onboarding deliberately suppresses the "where i'd push"
  // recommendation card. The data lands on the brain (mag's stamped
  // each rec with id + status='pending'), but the founder triages it
  // post-onboarding on /home as "recommended next actions" — not
  // here, where the goal is just "what mag pulled, then forward." See
  // packages/agents/src/agents/competition-analyst.ts for the agent's
  // ICP/voice/product fitness rules that gate what lands in
  // recommendations[] in the first place.
  //
  // Per-competitor agree / discard IS appropriate here because it's
  // about data correctness ("did mag get who you're up against right?"),
  // not strategic decision-making. Founder confirms the read or drops
  // a misread; either signal feeds future agents.

  return (
    <>
      <div className="mt-3 flex items-start gap-4">
        <div className="mag-stagger-in shrink-0" style={{ animationDelay: '120ms' }}>
          <Mag size={64} mood="smug" trackCursor />
        </div>
        <div className="min-w-0 flex-1">
          <h1
            className="mag-stagger-in font-display text-[34px] sm:text-[48px] font-black leading-[1.02] tracking-[-0.04em]"
            style={{ animationDelay: '180ms' }}
          >
            <Trans>here's who you're up against.</Trans>
          </h1>
          <p
            className="mag-stagger-in mt-3 font-serif text-[17px] leading-[1.5] text-[var(--ink)]/80"
            style={{ animationDelay: '260ms' }}
          >
            <Trans>
              “here's the read. i'll surface action paths from each move on your home feed once we
              wrap setup — you can decide what to chase from there.”
            </Trans>
          </p>
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-4">
        {/* ── Competitors card ─────────────────────────────────────── */}
        <section
          className="mag-stagger-in card-lift rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--plum)] hover:shadow-[6px_6px_0_var(--plum)] sm:p-6"
          style={{ animationDelay: '340ms' }}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
                {'↘'} <Trans>who you're up against</Trans>
              </p>
              <h2 className="mt-1 font-display text-[20px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
                {primaryLabel ? (
                  <Plural
                    value={landscape.competitors.length}
                    one="# competitor in {primaryLabel}."
                    other="# competitors in {primaryLabel}."
                  />
                ) : (
                  <Plural
                    value={landscape.competitors.length}
                    one="# competitor landed."
                    other="# competitors landed."
                  />
                )}
              </h2>
            </div>
            <p className="line-clamp-2 max-w-[400px] text-[12px] leading-[1.45] text-[var(--ink)]/55">
              “{landscape.summary}”
            </p>
          </div>

          {/* The other markets are RUNNING, not skipped. Shown only when there
              are any, so a single-market brand sees exactly what it saw
              before. Named individually rather than counted ("5 more
              markets") because the founder picked them by name and wants to
              see their own list come back. */}
          {pendingMarkets.length > 0 ? (
            <p className="mt-3 rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 font-serif text-[13px] leading-[1.5] text-[var(--ink)]/75">
              <Trans>
                “still mapping {pendingLabel} — those land on your home feed as they finish.”
              </Trans>
            </p>
          ) : null}

          {visibleCompetitors.length > 0 ? (
            <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
              {visibleCompetitors.map((c) => {
                const competitorId = c.id ?? c.url;
                const status = c.status ?? 'pending';
                const accepted = status === 'accepted';
                const pending = actions.isPending(competitorId);
                return (
                  <li
                    key={competitorId}
                    className={`rounded-lg border px-3 py-2.5 transition-colors ${
                      accepted
                        ? 'border-[var(--sage)]/40 bg-[var(--sage)]/8'
                        : 'border-[var(--line)] bg-[var(--paper)]/40'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-[3px] inline-block min-w-[64px] rounded-full border px-2 py-0.5 text-center font-mono text-[9px] font-semibold uppercase tracking-[0.08em] ${categoryChipClass(
                          c.category,
                        )}`}
                      >
                        {c.category}
                      </span>
                      <div className="min-w-0 flex-1">
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group inline-flex max-w-full items-baseline gap-1.5"
                        >
                          <span className="font-display text-[14px] font-semibold leading-tight text-[var(--ink)] underline decoration-[var(--ink)]/15 decoration-[1.5px] underline-offset-[3px] transition-colors group-hover:decoration-[var(--plum)]">
                            {c.name}
                          </span>
                          <span
                            aria-hidden="true"
                            className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/40 transition-all duration-200 group-hover:-translate-y-[1px] group-hover:translate-x-[1px] group-hover:text-[var(--plum)]"
                          >
                            ↗
                          </span>
                        </a>
                        <p className="mt-0.5 truncate font-mono text-[10.5px] uppercase tracking-[0.06em] text-[var(--ink)]/45">
                          {hostnameOf(c.url)}
                        </p>
                        {c.positioning ? (
                          <p className="mt-1.5 line-clamp-2 font-serif text-[13px] leading-[1.45] text-[var(--ink)]/70">
                            {c.positioning}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    {/* Right-aligned action row (CommitMind design rule). */}
                    <div className="mt-2.5 flex items-center justify-end gap-1.5">
                      {pending ? (
                        <span className="inline-flex items-center gap-1.5 rounded-md border border-[var(--spell)] bg-[var(--spell)]/15 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/70">
                          <Trans>
                            <span
                              aria-hidden="true"
                              className="inline-block h-3 w-3 animate-spin rounded-full border-[1.5px] border-[var(--ink)]/30 border-t-[var(--ink)]"
                            />
                            filing…
                          </Trans>
                        </span>
                      ) : accepted ? (
                        <>
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--sage)]/40 bg-[var(--sage)]/12 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--sage)]">
                            {'✓'} <Trans>you agreed</Trans>
                          </span>
                          <button
                            type="button"
                            onClick={() => actions.unagreeCompetitor(competitorId)}
                            className="rounded-md border border-transparent px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/55 hover:text-[var(--pulse)]"
                          >
                            <Trans>undo</Trans>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => actions.agreeCompetitor(competitorId)}
                            title={t`yes, this is a real competitor`}
                            className="btn btn-spell btn-sm"
                          >
                            {'✓'} <Trans>agree</Trans>
                          </button>
                          <button
                            type="button"
                            onClick={() => actions.discardCompetitor(competitorId)}
                            title={t`not actually a competitor — drop it`}
                            className="rounded-md border border-transparent px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/55 hover:text-[var(--pulse)]"
                          >
                            <Trans>disagree</Trans>
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-4 font-serif text-[13px] leading-[1.5] text-[var(--ink)]/65">
              <Trans>
                you've dropped everyone i found. you can add competitors you know about from the
                brand hub later — i'll re-run the analysis with your list.
              </Trans>
            </p>
          )}

          {remainingCompetitors > 0 ? (
            <p className="mt-3 text-[11.5px] text-[var(--ink)]/55">
              <Trans>
                + {remainingCompetitors} more on your home feed. open the{' '}
                <span className="font-mono text-[10px] uppercase">competition</span> tab to see them
                all.
              </Trans>
            </p>
          ) : null}
        </section>

        {/* ── SWOT / FODA summary ──────────────────────────────────── */}
        <SwotSummary swot={landscape.swot} />

        {/* Recommendations card intentionally hidden during onboarding.
            Mag's recs (with id + status='pending') still land on the
            brain via the agent; the founder triages them on /home as
            "recommended next actions" after onboarding wraps. Keeps
            this step focused on "here's who you're up against" without
            asking the founder to triage strategic moves mid-setup. */}
      </div>

      <div
        className="mag-stagger-in mt-10 flex items-center justify-end"
        style={{ animationDelay: '500ms' }}
      >
        <button type="button" onClick={onContinue} className="btn">
          <Trans>continue to home</Trans>
          <span className="btn-arrow">→</span>
        </button>
      </div>
    </>
  );
}

// ─── SWOT / FODA summary ──────────────────────────────────────────
// The competition agent already wrote the brand's SWOT (vs the set).
// Surface the SHORT read here — top 3 per quadrant — not the full
// breakdown, which lives on the competition tab + the brain.

function SwotSummary({ swot }: { swot: CompetitiveLandscape['swot'] }) {
  const { t } = useLingui();

  const [open, setOpen] = useState(false);
  const total =
    swot.strengths.length +
    swot.weaknesses.length +
    swot.opportunities.length +
    swot.threats.length;
  if (total === 0) return null;
  return (
    <section
      className="mag-stagger-in rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6"
      style={{ animationDelay: '420ms' }}
    >
      {/* Collapsed by default — the read is here when the founder wants
          it, but it doesn't pile height onto an already-tall results
          page. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-4 text-left"
      >
        <div className="min-w-0">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            {'↘'} <Trans>where you stand</Trans>
          </p>
          <h2 className="mt-1 font-display text-[20px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
            <Trans>your swot, in brief.</Trans>
          </h2>
          {!open ? (
            <p className="mt-1 text-[12px] text-[var(--ink)]/55">
              <Trans>
                <Plural value={total} one="# point" other="# points" /> on where you stand vs the
                set — strengths, gaps, openings, risks.
              </Trans>
            </p>
          ) : null}
        </div>
        <span className="mt-0.5 shrink-0 font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--ink)]/45 transition-colors group-hover:text-[var(--plum)]">
          {open ? <Trans>× hide</Trans> : <Trans>expand ↓</Trans>}
        </span>
      </button>

      {open ? (
        <div className="mag-fade-in">
          <p className="mt-3 text-[12px] text-[var(--ink)]/55">
            <Trans>the short read — the full breakdown lives on your competition tab.</Trans>
          </p>
          {/* Canonical 2×2 SWOT matrix: internal (strengths/weaknesses) on
              top, external (opportunities/threats) below; helpful left,
              harmful right. Tinted quadrants make it read as a matrix. */}
          <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
            <SwotQuad
              label={t`strengths`}
              glyph="+"
              cardClass="border-[var(--sage)]/35 bg-[var(--sage)]/8"
              headClass="text-[var(--sage)]"
              dotClass="bg-[var(--sage)]"
              items={swot.strengths}
            />
            <SwotQuad
              label={t`weaknesses`}
              glyph="−"
              cardClass="border-[var(--pulse)]/30 bg-[var(--pulse)]/8"
              headClass="text-[var(--pulse)]"
              dotClass="bg-[var(--pulse)]"
              items={swot.weaknesses}
            />
            <SwotQuad
              label={t`opportunities`}
              glyph="↗"
              cardClass="border-[var(--plum)]/30 bg-[var(--plum)]/8"
              headClass="text-[var(--plum)]"
              dotClass="bg-[var(--plum)]"
              items={swot.opportunities}
            />
            <SwotQuad
              label={t`threats`}
              glyph="!"
              cardClass="border-[#d4a017]/40 bg-[#d4a017]/10"
              headClass="text-[#7a5a05]"
              dotClass="bg-[#d4a017]"
              items={swot.threats}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}

function SwotQuad({
  label,
  glyph,
  cardClass,
  headClass,
  dotClass,
  items,
}: {
  label: string;
  glyph: string;
  cardClass: string;
  headClass: string;
  dotClass: string;
  items: readonly string[];
}) {
  const top = items.slice(0, 3);
  return (
    <div className={`flex flex-col rounded-xl border p-4 ${cardClass}`}>
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={`flex h-5 w-5 items-center justify-center rounded-full font-mono text-[11px] font-bold text-white ${dotClass}`}
        >
          {glyph}
        </span>
        <p className={`font-mono text-[10.5px] font-bold uppercase tracking-[0.12em] ${headClass}`}>
          {label}
        </p>
      </div>
      {top.length > 0 ? (
        <ul className="mt-2.5 flex flex-col gap-2">
          {top.map((t) => (
            <li key={t} className="text-[12.5px] leading-[1.45] text-[var(--ink)]/85">
              {t}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2.5 text-[12px] italic text-[var(--ink)]/40">
          <Trans>nothing flagged.</Trans>
        </p>
      )}
    </div>
  );
}

// ─── useCompetitorActions ────────────────────────────────────────
// Founder-triage mutations on brain.competitiveLandscape.competitors:
//   - agreeCompetitor(id)   → status='accepted', stamps acceptedAt
//   - unagreeCompetitor(id) → reverts to status='pending' (undo)
//   - discardCompetitor(id) → filter from competitors[] (founder
//                              disagrees — mag misread)
//
// Same queueRef-serialized pattern as useReviewActions in
// brain-review-lists.tsx — fast clicks chain through mutateAsync so
// the cache reflects every prior click before the next patch is built.

interface CompetitorActions {
  agreeCompetitor: (id: string) => void;
  unagreeCompetitor: (id: string) => void;
  discardCompetitor: (id: string) => void;
  isPending: (id: string) => boolean;
}

function useCompetitorActions(brandId: string): CompetitorActions {
  const queryClient = useQueryClient();
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());

  type Action =
    | { kind: 'agree'; id: string }
    | { kind: 'unagree'; id: string }
    | { kind: 'discard'; id: string };

  const mutation = useMutation({
    mutationFn: async (action: Action) => {
      const cached = queryClient.getQueryData(brandBrainQuery(brandId).queryKey) as
        | BrandBrainEnvelope
        | undefined;
      const landscape = cached?.brandBrain?.competitiveLandscape;
      if (!landscape) throw new Error('landscape not in cache — refresh the page');
      const patched = applyCompetitorAction(landscape, action);
      return saveBrandBrain({ competitiveLandscape: patched }, brandId);
    },
    onSuccess: (envelope, action) => {
      queryClient.setQueryData(brandBrainQuery(brandId).queryKey, envelope);
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(action.id);
        return next;
      });
    },
    onError: (err: Error, action) => {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(action.id);
        return next;
      });
      // Three whole sentences, not one with a verb interpolated in:
      // Spanish inflects the verb with the rest of the clause, so a
      // {verb} hole would hand a translator a sentence they cannot make
      // agree.
      const failed =
        action.kind === 'agree'
          ? msg`couldn't agree: ${err.message}`
          : action.kind === 'unagree'
            ? msg`couldn't undo: ${err.message}`
            : msg`couldn't discard: ${err.message}`;
      toast.error(i18n._(failed));
    },
  });

  const queueAction = (action: Action) => {
    if (pendingIds.has(action.id)) return;
    setPendingIds((prev) => new Set(prev).add(action.id));
    queueRef.current = queueRef.current
      .catch(() => undefined)
      .then(() => mutation.mutateAsync(action))
      .catch(() => undefined);
  };

  return {
    agreeCompetitor: (id) => queueAction({ kind: 'agree', id }),
    unagreeCompetitor: (id) => queueAction({ kind: 'unagree', id }),
    discardCompetitor: (id) => queueAction({ kind: 'discard', id }),
    isPending: (id) => pendingIds.has(id),
  };
}

function applyCompetitorAction(
  landscape: CompetitiveLandscape,
  action: { kind: 'agree' | 'unagree' | 'discard'; id: string },
): CompetitiveLandscape {
  if (action.kind === 'discard') {
    return {
      ...landscape,
      competitors: landscape.competitors.filter((c) => (c.id ?? c.url) !== action.id),
    };
  }
  const now = new Date().toISOString();
  return {
    ...landscape,
    competitors: landscape.competitors.map((c) => {
      const competitorId = c.id ?? c.url;
      if (competitorId !== action.id) return c;
      if (action.kind === 'agree') {
        return { ...c, status: 'accepted' as const, acceptedAt: now };
      }
      // unagree — revert to pending and drop acceptedAt
      const { acceptedAt: _stripped, ...rest } = c;
      return { ...rest, status: 'pending' as const };
    }),
  };
}

// ─── Helpers ──────────────────────────────────────────────────────

/** Hostname extractor for the competitor row's `↗`-link label. Falls
 *  back to the raw URL if parsing fails so the label is never blank. */
function hostnameOf(rawUrl: string): string {
  try {
    return new URL(rawUrl).hostname.replace(/^www\./, '');
  } catch {
    return rawUrl;
  }
}

/** Splits the founder's textarea into a list of URL strings. Lines
 *  that don't look like URLs are dropped (they become founderHints
 *  upstream when the URL list ends up empty). */
function parseCompetitorUrls(input: string): string[] {
  const lines = input
    .split(/[\n,]+/g)
    .map((s) => s.trim())
    .filter(Boolean);
  const urls: string[] = [];
  for (const line of lines) {
    try {
      // Allow founders to paste a bare domain — prepend https when
      // there's no scheme so URL() doesn't throw.
      const candidate = /^https?:\/\//i.test(line) ? line : `https://${line}`;
      const u = new URL(candidate);
      if (u.hostname.includes('.')) urls.push(u.toString());
    } catch {
      // Not a URL — skip; will be passed as a hint upstream instead.
    }
  }
  return urls.slice(0, 8);
}

function skipReason(outcome: CompetitionAnalyzeOutcome | null): string {
  if (!outcome) return i18n._(msg`i'll do this one later.`);
  switch (outcome.outcome) {
    case 'no_identity':
      return i18n._(
        msg`your brand identity is still thin — we'll come back here once it firms up.`,
      );
    case 'no_brain':
    case 'no_brand':
      return i18n._(msg`i need a brain to ground this against. come back after onboarding wraps.`);
    case 'agent_crashed':
      // mag-voice + actionable: the start button below IS the retry,
      // so don't send the founder to home. previous copy was system-
      // tone ("the analysis pass crashed mid-way") + misleading
      // ("from your home feed") when the obvious retry is right there.
      return i18n._(
        msg`that one came back tangled — couldn't make a clean read of it. hit start again, should land this pass.`,
      );
    case 'cooldown_active':
    case 'still_fresh':
      return i18n._(msg`we already have a recent read — i'll show it to you on home.`);
    case 'card_created':
      return '';
  }
}
